import { motion } from "framer-motion";
import { BUSINESS } from "@/config";

const GalleryCard = ({ img, idx, tag, tagTone, bare = false }) => {
  // tagTone: "copper" | "neutral"
  const tagClass =
    tagTone === "copper"
      ? "bg-[#D47A4A] text-[#0B132B]"
      : "bg-[#0B132B]/85 text-[#D47A4A] border border-[#D47A4A]/40";
  return (
    <motion.figure
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.55, delay: idx * 0.06 }}
      className="group relative overflow-hidden rounded-sm border border-[#2C374D] hover:border-[#D47A4A]/60 transition-colors bg-[#151D33]"
      data-testid={`gallery-card-${idx + 1}`}
    >
      <div className="relative aspect-[4/3] w-full overflow-hidden">
        <img
          src={img.url}
          alt={img.caption}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700 ease-out"
        />
        {!bare && (
          <div
            className="absolute inset-0 bg-gradient-to-t from-[#0B132B] via-[#0B132B]/20 to-transparent opacity-90 group-hover:opacity-95 transition-opacity pointer-events-none"
            aria-hidden
          />
        )}
        <span
          className={`absolute top-3 left-3 text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-sm shadow-md ${tagClass}`}
        >
          {tag}
        </span>
        {!bare && (
          <figcaption className="absolute bottom-0 left-0 right-0 p-4 md:p-5">
            <p className="font-display text-base md:text-lg text-white tracking-wide">
              {img.caption.toUpperCase()}
            </p>
          </figcaption>
        )}
      </div>
      {bare && (
        <figcaption className="px-4 md:px-5 py-3 md:py-4 border-t border-[#2C374D] bg-[#151D33]">
          <p className="font-display text-sm md:text-base text-white tracking-wide">
            {img.caption.toUpperCase()}
          </p>
        </figcaption>
      )}
    </motion.figure>
  );
};

const Gallery = () => {
  const { commercial, projects } = BUSINESS.gallery;

  return (
    <section
      id="gallery"
      className="relative py-20 md:py-28 bg-[#0B132B]"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* ── Commercial Work ─────────────────────────────────── */}
        <div className="mb-16 md:mb-20" data-testid="gallery-commercial-group">
          <div className="flex items-end justify-between gap-4 mb-6 md:mb-8 pb-4 border-b border-[#2C374D]">
            <h3 className="font-display text-2xl md:text-3xl text-white tracking-wide">
              COMMERCIAL WORK
            </h3>
            <span className="text-[10px] md:text-xs uppercase tracking-[0.22em] text-[#A0ABC0]">
              {commercial.length} Projects
            </span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 md:gap-6">
            {commercial.map((img, i) => (
              <GalleryCard
                key={img.url}
                img={img}
                idx={i}
                tag="Completed"
                tagTone="copper"
              />
            ))}
          </div>
        </div>

        {/* ── Project Work (Before / After) ───────────────────── */}
        <div data-testid="gallery-projects-group">
          <div className="flex items-end justify-between gap-4 mb-6 md:mb-8 pb-4 border-b border-[#2C374D]">
            <h3 className="font-display text-2xl md:text-3xl text-white tracking-wide">
              DOMESTIC WORK
            </h3>
            <span className="text-[10px] md:text-xs uppercase tracking-[0.22em] text-[#A0ABC0]">
              Before &amp; After
            </span>
          </div>

          <div className="space-y-10 md:space-y-14">
            {projects.map((proj, pIdx) => (
              <div
                key={proj.label}
                data-testid={`gallery-project-pair-${pIdx + 1}`}
              >
                <div className="flex items-center gap-3 mb-4 md:mb-5">
                  <span className="text-[10px] uppercase tracking-[0.3em] text-[#D47A4A]">
                    /{String(pIdx + 1).padStart(2, "0")}
                  </span>
                  <p className="font-display text-lg md:text-xl text-white tracking-wide">
                    {proj.label.toUpperCase()}
                  </p>
                  <div className="flex-1 h-px bg-[#2C374D]" aria-hidden />
                </div>
                <div
                  className={`grid grid-cols-1 gap-5 md:gap-6 ${
                    proj.items.length === 3
                      ? "sm:grid-cols-2 lg:grid-cols-3"
                      : "sm:grid-cols-2"
                  }`}
                >
                  {proj.items.map((img, i) => (
                    <GalleryCard
                      key={img.url}
                      img={img}
                      idx={i}
                      tag={img.type === "before" ? "Before" : "After"}
                      tagTone={img.type === "before" ? "neutral" : "copper"}
                      bare
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Gallery;
